"use client";

import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { UserReportsTable } from "./UserReportsTable";
import { UserReportsSkeleton } from "./UserReportsSkeleton";
import { useAuth } from "@/contexts/AuthContext";
import { userReportsService } from "@/services/userReportsService";
import { toast } from "sonner";
import { DocumentData } from "firebase/firestore";
import { useTenancy } from "@/contexts/TenancyContext";

interface QuizAttempt {
  attemptNumber: number;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  timestamp: Date | null;
  passed: boolean;
}

interface PolicyQuizDetails {
  policyId: string;
  lastAttempt?: QuizAttempt;
  bestAttempt?: QuizAttempt;
  totalAttempts: number;
}

interface UserData {
  id: string;
  name: string;
  role: string;
  assignedPolicies: number;
  readingPercentage: number;
  quizPercentage: number;
  status: string;
  statusColor: string;
  policyDocs: DocumentData[];
  quizDetails: PolicyQuizDetails[];
  completedQuizzes: number;
  totalQuizzes: number;
}

export function UserReportsContent() {
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();
  const [loading, setLoading] = useState(true);
  const [companyID, setCompanyID] = useState("");
  const [tenancyName, setTenancyName] = useState("");
  const [usersData, setUsersData] = useState<UserData[]>([]);

  useEffect(() => {
    if (!activeTenancy || !activeTenancy.id || ["", "Loading..."]?.includes(activeTenancy.id)) return;
    const fetchUserData = async () => {
      try {
        if (!user) return;

        // Get company ID from user document
        const companyID = activeTenancy?.id;

        if (!companyID) {
          throw new Error("User data not found or missing company ID");
        }

        setCompanyID(companyID);

        // Fetch company info
        const companyData = await userReportsService.getCompanyInfo(companyID);

        if (!companyData) {
          throw new Error("Company data not found");
        }

        setTenancyName(companyData.customerFriendlyName);

        // Get organization users and their data
        await getAssignedPoliciesUsers(companyID);
      } catch (error: any) {
        console.error("Error fetching user data:", error);
        toast.error("Error loading user data");
        setLoading(false);
      }
    };

    if (user) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [user, activeTenancy]);

  const getAssignedPoliciesUsers = async (companyID: string) => {
    try {
      setLoading(true);

      // Get all users in the company using the service
      const companyUsers = await userReportsService.getCompanyUsers(companyID);

      if (!companyUsers || companyUsers.length === 0) {
        setUsersData([]);
        return;
      }

      // Process each user in parallel
      const processedUsers = await Promise.all(
        companyUsers.map(async (companyUser: any) => {
          try {
            const userUID = companyUser.userID;

            // Get user info
            const userData = await userReportsService.getUserInfo(userUID);
            if (!userData) return null;

            // Get assigned policies
            const policyDocs = await userReportsService.getUserPolicies(userUID);

            // Process quiz results for a user
            // DED-1239: Returns both quizDetails and count of policies that have quizzes
            const processUserQuizzes = async (userId: string, policyDocs: any[], companyID: string): Promise<{ quizDetails: PolicyQuizDetails[], policiesWithQuizzes: number }> => {
              const quizDetails: PolicyQuizDetails[] = [];
              let policiesWithQuizzes = 0;

              for (const policy of policyDocs) {
                try {
                  const policyData = policy.data();
                  const policyId = policyData.reference;
                  
                  // Skip template policy
                  if (policyId === 'template') {
                    continue;
                  }

                  // DED-1239: Check if this policy has a quiz available (PDF policies don't have quizzes)
                  const hasQuiz = await userReportsService.policyHasQuiz(policyId, companyID);
                  if (!hasQuiz) {
                    // Skip policies without quizzes (e.g., PDF policies)
                    continue;
                  }
                  
                  // Count this policy as having a quiz
                  policiesWithQuizzes++;

                  // Get quiz results for this policy
                  const quizResults = await userReportsService.getPolicyQuizResults(
                    userId,
                    policyId,
                    companyID
                  );

                  if (quizResults) {
                    const attempts: QuizAttempt[] = [];
                    let bestScore = 0;
                    let lastAttempt: QuizAttempt | undefined;
                    let totalAttempts = 0;

                    // Process each attempt
                    for (const [attemptKey, attemptData] of Object.entries(quizResults)) {
                      if (attemptKey === 'attempt_number') continue;

                      const attemptNumber = parseInt(attemptKey.split('_')[1]);
                      
                      // Calculate total questions from the attempt data
                      const questionIndices = new Set<number>();
                      
                      // Find all unique question numbers
                      Object.keys(attemptData).forEach(key => {
                        const match = key.match(/^(\d+)(?:CorrectAnswer|GivenAnswer|TimeAnswered)$/);
                        if (match) {
                          questionIndices.add(parseInt(match[1]));
                        }
                      });
                      
                      const totalQuestions = questionIndices.size;
                      
                      // Calculate correct answers by comparing each question's answer
                      let correctAnswers = 0;
                      for (const q of questionIndices) {
                        const correctKey = `${q}CorrectAnswer`;
                        const givenKey = `${q}GivenAnswer`;
                        
                        if (attemptData[correctKey] && attemptData[givenKey] && 
                            attemptData[correctKey] === attemptData[givenKey]) {
                          correctAnswers++;
                        }
                      }
                      
                      // Fallback to the provided correctlyAnswered if our calculation fails
                      const verifiedCorrectAnswers = totalQuestions > 0 ? correctAnswers : 
                        parseInt(attemptData.correctlyAnswered || '0');
                      
                      const score = totalQuestions > 0 
                        ? Math.round((verifiedCorrectAnswers / totalQuestions) * 100) 
                        : 0;
                      
                      const attempt: QuizAttempt = {
                        attemptNumber,
                        score,
                        correctAnswers: verifiedCorrectAnswers,
                        totalQuestions,
                        timestamp: attemptData[`${totalQuestions}TimeAnswered`]?.toDate?.() || 
                                  attemptData[`1TimeAnswered`]?.toDate?.() || // Fallback to first question's timestamp
                                  null,
                        passed: verifiedCorrectAnswers >= Math.ceil(totalQuestions * 0.7) // 70% passing score
                      };

                      attempts.push(attempt);

                      // Track best attempt
                      if (attempt.score > bestScore) {
                        bestScore = attempt.score;
                      }

                      // Track last attempt
                      if (!lastAttempt || (attempt.timestamp &&
                        (!lastAttempt.timestamp || attempt.timestamp > lastAttempt.timestamp))) {
                        lastAttempt = attempt;
                      }

                      totalAttempts++;
                    }

                    const bestAttempt = attempts.length > 0
                      ? attempts.reduce((best, current) =>
                        (current.score > best.score) ? current : best, attempts[0])
                      : undefined;

                    quizDetails.push({
                      policyId,
                      lastAttempt,
                      bestAttempt,
                      totalAttempts
                    });
                  }
                } catch (error) {
                  console.error(`Error processing quiz for policy ${policy.id}:`, error);
                }
              }

              return { quizDetails, policiesWithQuizzes };
            };

            const { quizDetails, policiesWithQuizzes } = await processUserQuizzes(userUID, policyDocs, companyID);

            // Calculate policy completion
            const policyCount = policyDocs.length;
            const completedPolicies = policyDocs.filter(
              doc => doc.data().completed === "true"
            ).length;

            // Calculate quiz completion
            // DED-1239: Use policiesWithQuizzes as total, not quizDetails.length
            // This ensures PDF policies (which have no quiz) don't count toward the total
            const completedQuizzes = quizDetails.filter(q => q.bestAttempt?.passed).length;
            const totalQuizzes = policiesWithQuizzes;
            // If there are no policies with quizzes, show 100% (all quizzes complete = N/A)
            const quizPercentage = totalQuizzes > 0
              ? Math.round((completedQuizzes / totalQuizzes) * 100)
              : 100;

            const completionPercentage = policyCount > 0
              ? Math.round((completedPolicies / policyCount) * 100)
              : 0;

            // Determine status based on both reading and quiz completion
            let status = "Incomplete";
            let statusColor = "destructive";

            if (completionPercentage === 100 && quizPercentage === 100) {
              status = "Completed";
              statusColor = "success";
            } else if (completionPercentage > 0 || quizPercentage > 0) {
              status = "In Progress";
              statusColor = "warning";
            }

            // Build the final user object
            return {
              id: userUID,
              name: userData.displayName || `${userData.firstName} ${userData.lastName}` || userData.email?.split('@')[0] || 'Unknown User',
              role: companyUser.ismsRole || 'User',
              assignedPolicies: policyCount,
              readingPercentage: completionPercentage,
              quizPercentage,
              status,
              statusColor,
              policyDocs,
              quizDetails,
              completedQuizzes,
              totalQuizzes
            };
          } catch (error) {
            console.error("Error processing user:", companyUser.userID, error);
            return null;
          }
        })
      );

      // Filter out null entries (failed processing)
      setUsersData(processedUsers.filter(Boolean) as UserData[]);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error loading users");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container mx-auto px-4 py-8">
      <div className="glass-card">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-2xl font-bold">User Reports</h1>
            <p className="text-muted-foreground mt-1">
              View all of your organization's users' progress
            </p>
            {tenancyName && (
              <p className="text-sm text-muted-foreground">
                Organization: {tenancyName}
              </p>
            )}
          </div>

          <Card className="border-none shadow-none">
            <CardContent className="p-0 border-none shadow-none">
              {loading ? (
                <UserReportsSkeleton />
              ) : (
                <UserReportsTable users={usersData} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
